import { createRouter, createWebHistory } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { routes } from './routes'

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  scrollBehavior(_to, _from, savedPosition) {
    if (savedPosition) return savedPosition
    return { top: 0 }
  },
  routes,
})

// ---------------------------------------------------------------------------
// Navigation guards (FR-011)
// ---------------------------------------------------------------------------

router.beforeEach(async (to, _from, next) => {
  const authStore = useAuthStore()

  // Update document title
  document.title = to.meta.title ? `${to.meta.title} | Move 78` : 'Move 78'

  // Ensure auth state is initialized
  if (!authStore.isInitialized) {
    await authStore.initialize()
  }

  const isAuthPage = to.name === 'login' || to.name === 'register'

  // Redirect authenticated users away from login/register
  if (isAuthPage && authStore.isAuthenticated) {
    const next_ = to.query.next as string | undefined
    return next(next_ || { name: 'home' })
  }

  // Route requires authentication — redirect to login with ?next= param
  if (to.meta.requiresAuth && !authStore.isAuthenticated) {
    const encodedNext = encodeURIComponent(to.fullPath)
    authStore.setRedirectPath(to.fullPath)
    return next({ name: 'login', query: { next: encodedNext } })
  }

  // Route requires premium subscription
  if (to.meta.requiresPremium && !authStore.isPremium) {
    return next({ name: 'profile', query: { upgrade: 'true' } })
  }

  // Check if user needs to complete onboarding.
  //
  // Gated on `!allowAnonymous` rather than `requiresAuth` ([70]): the two are
  // not complements. A route that declares neither — easy to add by accident —
  // used to bypass onboarding silently. Keying off the explicit public marker
  // makes the omission fail safe (enforce) instead of fail open (skip), and
  // keeps anonymous generation working, since /generate, /, /privacy and the
  // auth pages all carry allowAnonymous by design (spec 047).
  if (
    authStore.isAuthenticated &&
    !authStore.hasCompletedOnboarding &&
    to.name !== 'onboarding' &&
    !to.meta.allowAnonymous
  ) {
    const skipOnboardingPaths = ['/profile', '/auth']
    if (!skipOnboardingPaths.some(p => to.path.startsWith(p))) {
      return next({ name: 'onboarding' })
    }
  }

  next()
})

router.afterEach(() => {
  // Track page view for analytics (if user consented)
})

export default router
